import {observer} from 'mobx-react-lite';
import React, {FC, useState} from 'react';
import {UserPlusIcon} from '@heroicons/react/24/outline';
import {useOrganizationContacts, useOrganizationInvites, useOrganizationMembers} from '../../react-sdk/queries';
import {Button, FlexFill, HelpDialog, PageTitle, SegmentedControl} from '../../Components';
import {ContactsTable, InvitesTable, MembersTable} from './Components';
import {AddContactDialog, InviteMemberDialog} from '../../Dialogs';
import {getCallerAccess} from './Components/memberAccess';
import {AppState} from '../../AppState';

export const Members: FC = observer(() => {
  const organizationId = AppState.currentProfile?.organization_id;
  const {isAdmin} = getCallerAccess();

  const [tab, setTab] = useState('members');
  const [inviting, setInviting] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const invitesQuery = useOrganizationInvites(organizationId);
  const membersQuery = useOrganizationMembers(organizationId);
  const contactsQuery = useOrganizationContacts(organizationId);

  const invitesCount = invitesQuery.data?.filter((invite) => invite.status === 'pending').length || 0;
  const membersCount = membersQuery.data?.length || 0;
  const contactsCount = contactsQuery.data?.length || 0;

  return (
    <div className="w-full max-w-[1100px] pb-10">
      <PageTitle>
        Members
        <FlexFill />
        <HelpDialog title="Members">
          <p>
            Members can perform basic operations, including creating and managing their own templates, as well as viewing and
            using templates shared by other Members in the same organization. (Note: Templates are private by default, and must
            be explicitly shared to allow this).
          </p>
          <p>
            Admins can perform the same functions as Members, but additionally can invite and manage other Members and Admins,
            create and manage API keys, and configure Brands, Webhooks, and other core features.
          </p>
          <p>
            Owners can perform the same functions as Admins, but additionally can manage billing plus creating and deleting organizations.
            Note that all organizations require at least one Owner.
          </p>
          <p>
            Contacts are people outside your organization to whom you regularly send envelopes for signing. They can't sign in to or see
            anything in your organization, and appear only in the contact picker when you send an envelope. Think of Contacts as an
            "address book".
          </p>
        </HelpDialog>
      </PageTitle>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl
          value={tab}
          onChange={setTab}
          options={[
            {key: 'members', label: `Members (${membersCount})`},
            {key: 'invites', label: `Invites (${invitesCount})`},
            {key: 'contacts', label: `Contacts (${contactsCount})`},
          ]}
        />
        {isAdmin &&
          (tab === 'contacts' ? (
            <Button size="small" label="Add contact" icon={<UserPlusIcon className="h-4 w-4" />} onClick={() => setAddingContact(true)} />
          ) : (
            <Button size="small" label="Invite member" icon={<UserPlusIcon className="h-4 w-4" />} onClick={() => setInviting(true)} />
          ))}
      </div>

      {tab === 'members' && <MembersTable />}
      {tab === 'invites' && <InvitesTable />}
      {tab === 'contacts' && <ContactsTable />}

      {inviting && (
        <InviteMemberDialog
          onClose={() => {
            setInviting(false);
            invitesQuery.refetch().catch((e: any) => console.log('Unknown Error', e));
            membersQuery.refetch().catch((e: any) => console.log('Unknown Error', e));
          }}
        />
      )}

      {addingContact && (
        <AddContactDialog
          onClose={() => {
            setAddingContact(false);
            contactsQuery.refetch().catch((e: any) => console.log('Unknown Error', e));
          }}
        />
      )}
    </div>
  );
});
